import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { CheckCircle2, XCircle, Download, DollarSign, Clock, ShieldCheck, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function ExpenseApprovalsPage() {
  const queryClient = useQueryClient();

  const { data: claims = [], isLoading } = useQuery({
    queryKey: ["expenses"],
    queryFn: async () => {
      const res = await apiClient.get("/expenses");
      return res.data;
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await apiClient.patch(`/expenses/${id}/status`, { status });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["expenses"]);
      toast.success("Expense status updated!");
    },
  });

  const handleExportQuickBooks = () => {
    window.open("http://localhost:8000/api/expenses/export/quickbooks", "_blank");
    toast.success("QuickBooks CSV export downloaded!");
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 font-sans">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-indigo-600" />
            Manager Expense Approvals & QuickBooks Export
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Review receipt OCR claims, approve reimbursements, and export GL items to QuickBooks CSV.
          </p>
        </div>
        <button
          onClick={handleExportQuickBooks}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-xl shadow-sm transition-all"
        >
          <FileSpreadsheet className="w-4 h-4" />
          Export QuickBooks CSV
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading expense claims...</div>
        ) : claims.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500 space-y-2">
            <Clock className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="font-semibold text-slate-700">No pending expense claims</p>
            <p className="text-xs text-slate-400">Claims submitted via OCR camera will appear here.</p>
          </div>
        ) : (
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                <th className="p-3.5">Merchant</th>
                <th className="p-3.5">Submitted By</th>
                <th className="p-3.5">Category</th>
                <th className="p-3.5">Date</th>
                <th className="p-3.5">Amount</th>
                <th className="p-3.5">Status</th>
                <th className="p-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {claims.map((claim) => (
                <tr key={claim._id} className="hover:bg-slate-50">
                  <td className="p-3.5 font-semibold text-slate-900">{claim.merchant_name}</td>
                  <td className="p-3.5 text-slate-500">{claim.submitted_by}</td>
                  <td className="p-3.5">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                      {claim.category}
                    </span>
                  </td>
                  <td className="p-3.5 text-slate-500 font-mono">
                    {new Date(claim.expense_date).toLocaleDateString()}
                  </td>
                  <td className="p-3.5 font-bold text-slate-900 font-mono">${claim.amount?.toFixed(2)}</td>
                  <td className="p-3.5">
                    <span
                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${claim.status === "APPROVED"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : claim.status === "REJECTED"
                            ? "bg-red-50 text-red-700 border border-red-200"
                            : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}
                    >
                      {claim.status}
                    </span>
                  </td>
                  <td className="p-3.5 text-right space-x-2">
                    {claim.status === "SUBMITTED" && (
                      <>
                        <button
                          onClick={() => updateStatusMutation.mutate({ id: claim._id, status: "APPROVED" })}
                          className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateStatusMutation.mutate({ id: claim._id, status: "REJECTED" })}
                          className="px-2.5 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg font-medium"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
